import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  TOP_GROUPS,
  expandGroupOrder,
  getGroupOrder,
  type ColumnKey,
  type TopGroupKey,
} from './columns';

type Props = {
  columnOrder: readonly ColumnKey[];
  onClose: () => void;
  onSave: (next: ColumnKey[]) => void;
};

// Grid-level "Reorder groups" modal. Mutates a DRAFT group sequence locally;
// Save emits the expanded column order to the parent (RolloutDetail), which
// applies it to local state only -- persistence stays in the preset-menu flow
// ("Save changes to current preset"). This keeps the group-reorder UX
// decoupled from the preset write path: users can experiment freely without
// surprise Firestore writes, and the existing save-to-preset semantics still
// apply.
export function GroupReorderModal({ columnOrder, onClose, onSave }: Props) {
  // Initialise draft from the current column order's group sequence.
  const [draft, setDraft] = useState<TopGroupKey[]>(() => getGroupOrder(columnOrder));

  // Esc closes (same as Cancel -- discards the draft).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const labelFor = (key: TopGroupKey) =>
    TOP_GROUPS.find((g) => g.key === key)?.label ?? key;

  const move = (key: TopGroupKey, direction: 'up' | 'down') => {
    setDraft((prev) => {
      const idx = prev.indexOf(key);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const next = [...prev];
      const a = next[idx]!;
      const b = next[swapIdx]!;
      next[idx] = b;
      next[swapIdx] = a;
      return next;
    });
  };

  const resetToFactory = () => {
    setDraft(TOP_GROUPS.map((g) => g.key));
  };

  const save = () => {
    onSave(expandGroupOrder(draft));
    onClose();
  };

  // Click-outside handled by the backdrop click (same as BudgetDriftModal
  // pattern in this codebase).
  return (
    <div
      className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl border border-slate-200 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reorder-groups-title"
      >
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 id="reorder-groups-title" className="text-base font-semibold text-slate-900">
            Reorder column groups
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Groups reorder globally. Columns within each group stay in their factory order.
          </p>
        </div>

        <ul className="p-3 space-y-1">
          {draft.map((key, i) => {
            const isFirst = i === 0;
            const isLast = i === draft.length - 1;
            return (
              <li
                key={key}
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-white"
              >
                <span className="flex-1 text-sm text-slate-800">{labelFor(key)}</span>
                <button
                  type="button"
                  onClick={() => move(key, 'up')}
                  disabled={isFirst}
                  aria-label={`Move ${labelFor(key)} up`}
                  className="text-xs px-1.5 py-0.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(key, 'down')}
                  disabled={isLast}
                  aria-label={`Move ${labelFor(key)} down`}
                  className="text-xs px-1.5 py-0.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  ↓
                </button>
              </li>
            );
          })}
        </ul>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={resetToFactory}
            className="text-xs text-slate-500 hover:text-slate-800 underline"
          >
            Reset to factory
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save}>Save</Button>
          </div>
        </div>

        <p className="px-5 pb-3 text-[11px] text-slate-400">
          Save applies to this session. Use <span className="font-medium">Save changes to current preset</span> in the View menu to persist.
        </p>
      </div>
    </div>
  );
}
